import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Home as HomeIcon,
    Hammer,
    Sparkles,
    PaintBucket,
    Wrench,
} from "lucide-react";
import FadeUp from "../components/FadeUp";
import FeaturedProject from "../components/FeaturedProject";
import { api } from "../lib/api";
import { STOCK, RESIDENTIAL_GALLERY } from "../lib/stock";
import { FEATURED_PROJECT_RESIDENTIAL } from "../lib/constants";

const SERVICES = [
    {
        icon: HomeIcon,
        title: "Whole-Home Renovation",
        body: "Layout-changing remodels, structural modifications, and full-scope refinishes that elevate the entire home.",
    },
    {
        icon: Sparkles,
        title: "Kitchens & Baths",
        body: "Comp-moving kitchen and bath upgrades. Cabinetry, counters, tile, plumbing, and lighting executed cleanly.",
    },
    {
        icon: Hammer,
        title: "Additions & Expansions",
        body: "Bonus rooms, primary suites, and finished basements that add livable square footage and value.",
    },
    {
        icon: PaintBucket,
        title: "Interior Finishes",
        body: "Drywall, interior & exterior painting, trim & carpentry, and flooring coordination.",
    },
    {
        icon: Wrench,
        title: "MEP Upgrades",
        body: "Electrical installations & upgrades, lighting systems, plumbing repairs, HVAC service.",
    },
    {
        icon: HomeIcon,
        title: "Roofing & Exterior",
        body: "Roof repairs, replacements, and drainage. Curb-appeal exterior work that lifts first-impression value.",
    },
];

export default function ResidentialRenovations() {
    const [photos, setPhotos] = useState([]);
    useEffect(() => {
        api.get("/photos", { params: { section: "residential" } })
            .then((r) => setPhotos(r.data || []))
            .catch(() => setPhotos([]));
    }, []);

    return (
        <main data-testid="residential-page" className="bg-[#050505]">
            <section className="relative pt-40 pb-24 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={STOCK.residentialHero}
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-transparent to-[#050505]" />
                </div>
                <div className="container-bevel relative z-10">
                    <FadeUp>
                        <span className="overline">Residential Renovations</span>
                    </FadeUp>
                    <FadeUp delay={1}>
                        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95] max-w-4xl">
                            Renovations that elevate{" "}
                            <span className="text-[#F5A623]">
                                liveability and value.
                            </span>
                        </h1>
                    </FadeUp>
                    <FadeUp delay={2}>
                        <p className="mt-8 text-lg text-white/65 max-w-2xl leading-relaxed">
                            High-impact residential remodels for homeowners who
                            want a better space today and a stronger asset
                            tomorrow. Built with the same scope discipline we
                            bring to commercial work.
                        </p>
                    </FadeUp>
                    <FadeUp delay={3}>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/contact"
                                data-testid="residential-hero-cta"
                                className="btn-primary"
                            >
                                Plan Your Renovation <ArrowRight size={16} />
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <section className="section-pad bg-[#080808] hairline-top">
                <div className="container-bevel">
                    <FadeUp>
                        <span className="overline">What We Renovate</span>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
                            Quality work, scoped for return.
                        </h2>
                    </FadeUp>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {SERVICES.map((s, i) => (
                            <FadeUp
                                key={s.title}
                                delay={i}
                                className="bg-[#0A0A0A] p-8 md:p-10"
                            >
                                <s.icon
                                    size={26}
                                    className="text-[#F5A623]"
                                    strokeWidth={1.5}
                                />
                                <h3 className="font-display text-xl mt-5 text-white">
                                    {s.title}
                                </h3>
                                <p className="text-white/60 mt-3 text-sm leading-relaxed">
                                    {s.body}
                                </p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <section
                className="section-pad bg-[#080808] hairline-top"
                data-testid="residential-featured-project-section"
            >
                <div className="container-bevel">
                    <FadeUp>
                        <FeaturedProject
                            testId="residential-featured-project"
                            eyebrow="Featured Project"
                            title="Whole-Home Renovation"
                            location="Tulsa, OK"
                            description="A complete residential renovation managed scope-to-finish. Comp-aligned upgrades, technical precision on every trade, and a punch list closed cleanly before handover."
                            image={FEATURED_PROJECT_RESIDENTIAL}
                            reverse
                            cta={{
                                label: "Plan Your Renovation",
                                to: "/contact",
                            }}
                        />
                    </FadeUp>
                </div>
            </section>

            <section
                className="section-pad"
                data-testid="residential-portfolio-section"
            >
                <div className="container-bevel">
                    <FadeUp>
                        <span className="overline">Recent Projects</span>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                            Real homes. Real results.
                        </h2>
                    </FadeUp>

                    {photos.length === 0 ? (
                        <>
                            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                                {RESIDENTIAL_GALLERY.map((g, i) => (
                                    <div
                                        key={i}
                                        className="bg-[#0A0A0A] group"
                                    >
                                        <div className="relative aspect-[4/3] overflow-hidden">
                                            <img
                                                src={g.src}
                                                alt={g.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>
                                        <div className="p-5 border-t border-white/10">
                                            <div className="font-display text-lg text-white">
                                                {g.title}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p
                                className="mt-4 text-[10px] uppercase tracking-[0.2em] text-white/40"
                                data-testid="residential-gallery-disclosure"
                            >
                                Representative imagery
                            </p>
                        </>
                    ) : (
                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                            {photos.map((p) => (
                                <div
                                    key={p.id}
                                    className="bg-[#0A0A0A] group"
                                >
                                    <div className="relative aspect-[4/3] overflow-hidden">
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.storage_path}`}
                                            alt={p.title || "Residential project"}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    {(p.title || p.description) && (
                                        <div className="p-5 border-t border-white/10">
                                            {p.title && (
                                                <div className="font-display text-lg text-white">
                                                    {p.title}
                                                </div>
                                            )}
                                            {p.description && (
                                                <div className="text-sm text-white/55 mt-1">
                                                    {p.description}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
}
